import styled, { css } from "styled-components";

import { pagePadding } from "_/style/layout";

export const Wrapper = styled.div`
  ${pagePadding}
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
  overflow: auto;
`;

export const Editor = styled.textarea`
  background-color: ${(p) => p.theme.background.secondary};
  border: 1px solid ${(p) => p.theme.border.default};
  border-radius: 0.25rem;
  color: ${(p) => p.theme.text.default};
  font-family: monospace;
  font-size: 0.875rem;
  min-height: 6rem;
  padding: 0.75rem;
  resize: vertical;
  width: 100%;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

export const Disclosure = styled.details`
  color: ${(p) => p.theme.text.muted};
  font-size: 0.75rem;
`;

export const DisclosureSummary = styled.summary`
  cursor: pointer;
  user-select: none;

  /* Native summary text has no vertical padding of its own, so it sits above */
  /* the taller Run button's centre without this. */
  padding-top: 0.5rem;
`;

export const DisclosureList = styled.dl`
  display: grid;
  grid-template-columns: auto auto;
  gap: 0.125rem 1rem;
  font-family: monospace;
  margin: 0.5rem 0 0;

  dd {
    margin: 0;
  }
`;

export const Notice = styled.p`
  color: ${(p) => p.theme.text.muted};
`;

export const ErrorMessage = styled.p`
  color: ${(p) => p.theme.color.palette.tomatoRed};
  font-family: monospace;
  font-size: 0.875rem;
  white-space: pre-wrap;
`;

export const Failure = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
`;

/**
 * Row count on the left, page controls on the right, sitting above the table.
 */
export const ResultHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;

  /* Notice is a paragraph, whose default margins would push the row apart. */
  ${Notice} {
    margin: 0;
  }
`;

export const Pagination = styled.nav`
  align-items: center;
  display: flex;
  gap: 0.25rem;
`;

/**
 * One position in the pagination control.
 *
 * Numbers, ellipses and arrows all share this box so that the control keeps a
 * single width: a narrower ellipsis would shift every button beside it as the
 * shape changes from page to page. The square also keeps the target above the
 * 24px minimum, which the text size alone does not.
 */
const pageSlot = css`
  align-items: center;
  display: inline-flex;
  font-size: 0.875rem;
  justify-content: center;
  min-height: 2rem;

  /* Wide enough for a three digit page number */
  min-width: 2.5rem;
  padding: 0 0.25rem;

  /* Uniform digit widths */
  font-variant-numeric: tabular-nums;
`;

export const PageButton = styled.button<{ $current: boolean }>`
  ${pageSlot}

  background-color: ${(p) =>
    p.$current ? p.theme.background.tertiary : "transparent"};
  border: 1px solid
    ${(p) => (p.$current ? p.theme.border.default : "transparent")};
  border-radius: 0.25rem;
  color: ${(p) => (p.$current ? p.theme.text.default : p.theme.text.muted)};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${(p) => p.theme.text.hover};
  }

  &:disabled {
    color: ${(p) => p.theme.text.disabled};
    cursor: default;
  }
`;

/**
 * Previous/next control. Same hit area as a page number, with a larger glyph so
 * the chevron is easier to aim at than 14px of arrow.
 */
export const PageStep = styled(PageButton)`
  font-size: 1.25rem;
`;

export const PageGap = styled.span`
  ${pageSlot}

  color: ${(p) => p.theme.text.muted};
`;
